import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { tableFiltersByTab, pageSize, roleTabs } from "./constants";
import ManageUserTabs from "./ManageUserTabs";
import ManageUserContent from "./ManageUserContent";
import { ArrowLeft } from "../AdminIcons";
import {
  useGetAllInstructorsQuery,
  useGetAllRolesQuery,
  useGetAllStudentsQuery,
  useGetRolesListQuery,
  useGetStaffWithPendingStatusQuery,
} from "../../services/user/userApi";
import { useGetPublicCoursesQuery } from "../../services/course/courseApi";

const ManageUserView = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Instructors");
  const [activeFilter, setActiveFilter] = useState(
    tableFiltersByTab.Instructors[0],
  );
  const [accessRoleStatusFilter, setAccessRoleStatusFilter] = useState("");
  const [accessRoleNameFilter, setAccessRoleNameFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);

  const instructorStatus =
    activeTab === "Instructors" && activeFilter !== "All"
      ? activeFilter.toLowerCase()
      : "";
  const studentStatus =
    activeTab === "Students" && activeFilter !== "All"
      ? activeFilter.toLowerCase()
      : "";

  const { data: instructorsResponse, isFetching: isInstructorsFetching } =
    useGetAllInstructorsQuery(
      {
        search: "",
        status: instructorStatus,
        page: currentPage,
        limit: pageSize,
      },
      {
        skip: activeTab !== "Instructors",
        refetchOnMountOrArgChange: true,
      },
    );

  const instructorPagination = instructorsResponse?.pagination;
  const instructorList = Array.isArray(instructorsResponse?.data)
    ? instructorsResponse.data
    : [];

  const { data: studentsResponse, isFetching: isStudentsFetching } =
    useGetAllStudentsQuery(
      {
        search: "",
        status: studentStatus,
        page: currentPage,
        limit: pageSize,
      },
      {
        skip: activeTab !== "Students",
        refetchOnMountOrArgChange: true,
      },
    );

  const studentPagination = studentsResponse?.pagination;
  const studentList = Array.isArray(studentsResponse?.data)
    ? studentsResponse.data
    : [];

  const accessRoleName =
    activeTab === "Access Role" && accessRoleNameFilter
      ? accessRoleNameFilter
      : "";

  const isInvitedUserFilter =
    activeTab === "Access Role" && activeFilter === "Invited User";

  const isAddedRoleFilter =
    activeTab === "Access Role" && activeFilter === "Added Role";

  const accessRoleQueryArgs = {
    role: accessRoleName,
    status: accessRoleStatusFilter,
    page: currentPage,
    limit: pageSize,
  };

  const pendingRoleQueryArgs = {
    role: accessRoleName,
    page: currentPage,
    limit: pageSize,
  };

  const { data: rolesResponse, isFetching: isAssignedRolesFetching, refetch: refetchAssignedRoles } =
    useGetAllRolesQuery(accessRoleQueryArgs, {
      skip:
        activeTab !== "Access Role" ||
        isInvitedUserFilter ||
        isAddedRoleFilter,
      refetchOnMountOrArgChange: true,
    });

  const { data: pendingRolesResponse, isFetching: isPendingRolesFetching, refetch: refetchPendingRoles } =
    useGetStaffWithPendingStatusQuery(pendingRoleQueryArgs, {
      skip:
        activeTab !== "Access Role" ||
        !isInvitedUserFilter ||
        isAddedRoleFilter,
      refetchOnMountOrArgChange: true,
    });

  const accessRoleResponse = isInvitedUserFilter
    ? pendingRolesResponse
    : rolesResponse;

  const isAccessRolesFetching = isInvitedUserFilter
    ? isPendingRolesFetching
    : isAssignedRolesFetching;

  const refreshAccessRoleTable = useCallback(() => {
    if (activeTab !== "Access Role" || isAddedRoleFilter) {
      return Promise.resolve();
    }

    if (isInvitedUserFilter) {
      return refetchPendingRoles();
    }

    return refetchAssignedRoles();
  }, [
    activeTab,
    isAddedRoleFilter,
    isInvitedUserFilter,
    refetchAssignedRoles,
    refetchPendingRoles,
  ]);

  const accessRolePagination = accessRoleResponse?.pagination;
  const accessRoleList = Array.isArray(accessRoleResponse?.data)
    ? accessRoleResponse.data
    : [];

  const {
    data: rolesListResponse,
    isFetching: isRolesListFetching,
    refetch: refetchRolesList,
  } = useGetRolesListQuery(undefined, {
    skip: activeTab !== "Access Role",
    refetchOnMountOrArgChange: true,
  });

  const refreshRolesList = useCallback(() => {
    if (activeTab !== "Access Role") {
      return Promise.resolve();
    }

    return refetchRolesList();
  }, [activeTab, refetchRolesList]);

  const roleFilterOptions = useMemo(() => {
    const roles = rolesListResponse?.data ?? [];

    return [
      { value: "All", label: "All" },
      ...roles.map((role) => ({
        value: role.name,
        label: role.name,
      })),
    ];
  }, [rolesListResponse]);

  const { data: publicCoursesResponse } = useGetPublicCoursesQuery();



  const publicCourses = Array.isArray(publicCoursesResponse?.data)
    ? publicCoursesResponse.data
    : [];


  const courseNameById = publicCourses.reduce((accumulator, course) => {
    const courseId = course?._id || course?.id;

    if (courseId) {
      accumulator[courseId] = course?.courseTitle || courseId;
    }

    return accumulator;
  }, {});

  const apiInstructors = instructorList.map((instructor) => ({
    id: instructor._id || instructor.id,
    firstName: instructor.firstName || "",
    lastName: instructor.lastName || "",
    name:
      [instructor.firstName, instructor.lastName].filter(Boolean).join(" ") ||
      "-",
    email: instructor.email || "-",
    course: Array.isArray(instructor.instructor?.coursePreferences)
      ? instructor.instructor.coursePreferences
          .map((courseId) => courseNameById[courseId] || courseId)
          .filter(Boolean)
          .join(", ") || null
      : null,
    profilePicture: instructor.profilePicture || null,
    status: instructor.accountStatus,
  }));

  const apiStudents = studentList.map((student) => ({
    id: student._id,
    firstName: student.firstName || "",
    lastName: student.lastName || "",
    name:
      [student.firstName, student.lastName].filter(Boolean).join(" ") || "-",
    email: student.email || "-",
    course: student.courses?.length ? student.courses : ["no course"],
    profilePicture: student.profilePicture || null,
    status: student.accountStatus || student.status,
    note: student.note || "",
  }));

  const apiAccessRoles = accessRoleList.map((role) => ({
    id: role._id || "",
    userEmail: role.email || "",
    accessRole: role.role || "",
    status: role.roleStatus,
  }));

  const apiRoleDefinitions = (rolesListResponse?.data ?? []).map((role) => ({
    id: String(role._id || role.id || ""),
    roleName: role.name || "-",
    description: role.description || "-",
    routePermissions: Array.isArray(role.routePermission)
      ? role.routePermission
      : [],
  }));

  const tableFilters = tableFiltersByTab[activeTab] || [];
  useEffect(() => {
    setActiveFilter(tableFiltersByTab[activeTab][0]);
    setAccessRoleStatusFilter("");
    setAccessRoleNameFilter("");
    setCurrentPage(1);
    setSelectedRows([]);
  }, [activeTab]);

  const rows =
    activeTab === "Instructors"
      ? apiInstructors
      : activeTab === "Students"
        ? apiStudents
        : isAddedRoleFilter
          ? apiRoleDefinitions
          : apiAccessRoles;

  const isServerPaginated =
    activeTab === "Instructors" ||
    activeTab === "Students" ||
    (activeTab === "Access Role" && !isAddedRoleFilter);

  const activePagination =
    activeTab === "Instructors"
      ? instructorPagination
      : activeTab === "Students"
        ? studentPagination
        : activeTab === "Access Role"
          ? accessRolePagination
          : null;

  const activeList =
    activeTab === "Instructors"
      ? instructorList
      : activeTab === "Students"
        ? studentList
        : activeTab === "Access Role"
          ? isAddedRoleFilter
            ? apiRoleDefinitions
            : accessRoleList
          : [];

  const isActiveTabFetching =
    (activeTab === "Instructors" && isInstructorsFetching) ||
    (activeTab === "Students" && isStudentsFetching) ||
    (activeTab === "Access Role" &&
      !isAddedRoleFilter &&
      isAccessRolesFetching) ||
    (activeTab === "Access Role" &&
      isAddedRoleFilter &&
      isRolesListFetching);

  const totalPages = isServerPaginated
    ? Math.max(1, activePagination?.totalPages ?? 1)
    : Math.max(1, Math.ceil(rows.length / pageSize));

  const safePage = isServerPaginated
    ? currentPage
    : Math.min(currentPage, totalPages);

  const pageRows = isServerPaginated
    ? isActiveTabFetching && activeList.length === 0
      ? []
      : rows
    : rows.slice((safePage - 1) * pageSize, safePage * pageSize);

  const totalCount = isServerPaginated
    ? activeTab === "Instructors"
      ? (instructorPagination?.totalInstructors ?? rows.length)
      : activeTab === "Students"
        ? (studentPagination?.totalStudents ?? rows.length)
        : (accessRolePagination?.totalStaff ?? rows.length)
    : rows.length;
  const allVisibleSelected =
    pageRows.length > 0 &&
    pageRows.every((row) => selectedRows.includes(row.id));

  const toggleRow = (rowId) => {
    setSelectedRows((current) =>
      current.includes(rowId)
        ? current.filter((id) => id !== rowId)
        : [...current, rowId],
    );
  };

  const toggleVisibleRows = () => {
    const visibleIds = pageRows.map((row) => row.id);

    if (allVisibleSelected) {
      setSelectedRows((current) =>
        current.filter((id) => !visibleIds.includes(id)),
      );
      return;
    }

    setSelectedRows((current) =>
      Array.from(new Set([...current, ...visibleIds])),
    );
  };

  return (
    <section className="min-h-screen px-3 pb-7 pt-4 font-['Roboto'] sm:px-5 xl:pl-8 xl:pr-7">
      <div className="w-full max-w-[1515px]">
        <div className="bg-white px-4 pb-4 pt-5 xl:px-6 xl:pb-6">
          <button
            type="button"
            aria-label="Go back"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft />
          </button>
          <h2 className="mt-4 text-xl font-bold text-[#111827] sm:mt-5 sm:text-2xl">
            User Management
          </h2>

          <ManageUserTabs
            activeTab={activeTab}
            tabs={roleTabs}
            onTabChange={setActiveTab}
          />
        </div>

        <div className="mt-4 pt-[18px] pb-3 pl-4 xl:pl-7 bg-white">
          <ManageUserContent
            roleFilterOptions={roleFilterOptions}
            isRoleFilterLoading={isRolesListFetching}
            isInvitedUserFilter={isInvitedUserFilter}
            isAddedRoleFilter={isAddedRoleFilter}
            activeTab={activeTab}
            rows={rows}
            pageRows={pageRows}
            totalCount={totalCount}
            tableFilters={tableFilters}
            activeFilter={activeFilter}
            onFilterChange={(filter) => {
              setActiveFilter(filter);
              setAccessRoleNameFilter("");
              setAccessRoleStatusFilter("");
              setCurrentPage(1);
              setSelectedRows([]);
            }}
            accessRoleNameFilter={accessRoleNameFilter}
            onAccessRoleNameChange={(name) => {
              setAccessRoleNameFilter(name);
              setCurrentPage(1);
              setSelectedRows([]);
            }}
            accessRoleStatusFilter={accessRoleStatusFilter}
            onAccessRoleStatusChange={(status) => {
              setAccessRoleStatusFilter(status);
              setCurrentPage(1);
              setSelectedRows([]);
            }}
            selectedRows={selectedRows}
            onToggleRow={toggleRow}
            onToggleAll={toggleVisibleRows}
            allVisibleSelected={allVisibleSelected}
            isTableLoading={isActiveTabFetching}
            currentPage={safePage}
            totalPages={totalPages}
            onPrevious={() =>
              setCurrentPage((page) => Math.max(page - 1, 1))
            }
            onNext={() =>
              setCurrentPage((page) => Math.min(page + 1, totalPages))
            }
            onAccessRoleUpdated={refreshAccessRoleTable}
            onRolesListUpdated={refreshRolesList}
          />
        </div>
      </div>
    </section>
  );
};

export default ManageUserView;
